import React, { useState } from 'react'
import { AudienceSelection, SavedAudience } from '@marketing-milk/interfaces'
import { SurfacePalette } from '../../tokens'
import { useUpdateSprintPacket } from 'hooks/api/useSprintPackets'
import { SectionCard } from './SectionCard'
import { AudienceContent } from './AudienceContent'
import { AudienceSelectionEditor } from './AudienceSelectionEditor'
import { useAudienceDetailModal } from './useAudienceDetailModal'

interface Props {
  palette: SurfacePalette
  businessID: number
  packetID: number
  audiences: SavedAudience[]
  audienceSelection: AudienceSelection | null
}

/**
 * Edit-mode Audience section. Renders the AE-curated selection (resolved server
 * side onto `audiences`) and lets the AE edit which saved audiences appear and
 * which is primary. The selection persists through the shared packet update
 * path; the section chrome keeps its packet-local `section` anchor. Per-audience
 * comments are intentionally not wired here (gated until v1.3b).
 */
export const AudienceSection = ({
  palette,
  businessID,
  packetID,
  audiences,
  audienceSelection,
}: Props) => {
  const [editing, setEditing] = useState(false)
  const updatePacket = useUpdateSprintPacket(packetID)
  const { onOpenDetail, modals } = useAudienceDetailModal()

  const onSave = (selection: AudienceSelection) =>
    updatePacket.mutate({ audienceSelection: selection }, { onSuccess: () => setEditing(false) })

  return (
    <SectionCard
      palette={palette}
      sectionKey="audience-framework"
      title="Audience framework"
      commentAnchor={{
        type: 'section',
        id: `${packetID}:audience-framework`,
        label: 'Audience framework',
      }}
    >
      <AudienceContent
        palette={palette}
        audiences={audiences}
        primaryAudienceID={audienceSelection?.primaryAudienceID ?? null}
        editable
        onEditSelection={() => setEditing(true)}
        onOpenDetail={onOpenDetail}
      />
      {editing && (
        <AudienceSelectionEditor
          palette={palette}
          businessID={businessID}
          current={audienceSelection}
          saving={updatePacket.isLoading}
          onSave={onSave}
          onClose={() => setEditing(false)}
        />
      )}
      {modals}
    </SectionCard>
  )
}
